/**
 * Checkbox Component
 * Reusable checkbox input component.
 */

import { forwardRef, type InputHTMLAttributes } from 'react';
import styles from './Checkbox.module.css';

/**
 * Checkbox component props.
 */
interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Checkbox label */
  label: string;
  /** Error message */
  error?: string;
}

/**
 * Checkmark icon.
 */
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 3 4.5 8.5 2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * Reusable Checkbox component.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, className = '', id, ...props }, ref) => {
    const checkboxId = id || `checkbox-${label.toLowerCase().replace(/\s/g, '-')}`;

    return (
      <div className={`${styles.container} ${className}`}>
        <label className={styles.label} htmlFor={checkboxId}>
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            className={styles.input}
            {...props}
          />
          <span className={styles.checkmark}>
            <CheckIcon />
          </span>
          <span className={styles.labelText}>{label}</span>
        </label>
        {error && <span className={styles.error}>{error}</span>}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

